import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { UpperCasePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxChip } from '@onyx/angular';
import {
  ActionHelper,
  OnyxAvatarComponent,
  OnyxChipsComponent,
  OnyxDropdownDirective,
  OnyxFormMode,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxInformationRowComponent,
  OnyxModalComponent,
  OnyxOverlayPosition,
  OnyxTableComponent,
  OnyxToastService,
  OnyxTooltipDirective,
} from '@onyx/angular';
import { isString } from 'lodash';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import { ContractorPaymentComponent } from '../../../../common/components/contractor-payment/contractor-payment.component';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ModalsStore } from '../../../../common/stores/modals.store';
import { ContractorContactsComponent } from '../common/components/contractor-contacts/contractor-contacts.component';
import { getContractorsFormDocumentsColumns } from '../common/constants/contractors-form-documents-columns';
import { ContractorStatus } from '../common/enums/contractor-status';
import {
  CONTRACTOR_HEADER_ACTIONS,
  ContractorHelper,
} from '../common/helpers/contractor.helper';
import type {
  Contractor,
  SimplifiedContractor,
} from '../common/interfaces/contractor';
import { ContractorsService } from '../common/services/contractors.service';
import { ContractorModalBranchesComponent } from './contractor-modal-branches/contractor-modal-branches.component';
import { ContractorModalPointsOfInterestComponent } from './contractor-modal-points-of-interest/contractor-modal-points-of-interest.component';

export type ContractorModalData = Contractor | SimplifiedContractor | string;

enum ContractorModalSection {
  BRANCHES,
  POINTS_OF_INTEREST,
  DOCUMENTS,
  COMPANY,
}

@Component({
  selector: 'app-contractor-modal',
  imports: [
    OnyxModalComponent,
    OnyxIconButtonComponent,
    OnyxIconComponent,
    TranslatePipe,
    OnyxInformationRowComponent,
    OnyxChipsComponent,
    OnyxTableComponent,
    OnyxTooltipDirective,
    OnyxDropdownDirective,
    ContractorContactsComponent,
    ContractorModalBranchesComponent,
    OnyxAvatarComponent,
    UpperCasePipe,
    ContractorModalPointsOfInterestComponent,
    ContractorPaymentComponent,
  ],
  templateUrl: './contractor-modal.component.html',
  styleUrl: './contractor-modal.component.scss',
})
export class ContractorModalComponent {
  protected readonly data = inject<ContractorModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly contractorsService = inject(ContractorsService);
  private readonly toastService = inject(OnyxToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly contractorHelper = inject(ContractorHelper);
  private readonly modalsStore = inject(ModalsStore);

  protected readonly I18N = 'contractors';
  protected readonly COLUMNS = getContractorsFormDocumentsColumns(
    OnyxFormMode.ADD,
  );

  protected readonly ActionHelper = ActionHelper;
  protected readonly DictionaryCode = DictionaryCode;
  protected readonly ContractorModalSection = ContractorModalSection;
  protected readonly ContractorStatus = ContractorStatus;
  protected readonly OnyxOverlayPosition = OnyxOverlayPosition;

  private readonly uuid: string;

  protected contractor = signal<Contractor | null>(null);
  protected section = this.modalsStore.getActiveSection(
    ContractorModalComponent.name,
    () => this.sections(),
  );
  protected loading = signal(false);
  protected error = signal(false);
  protected contractor$ = new Subject<void>();
  protected close$ = new Subject<void>();

  protected options = computed(() =>
    this.contractor()
      ? this.contractorHelper.getOptions(
          this.contractor()!,
          { close$: this.close$ },
          CONTRACTOR_HEADER_ACTIONS,
        )
      : null,
  );
  protected isArchived = computed(() =>
    this.contractor() ? ContractorHelper.isArchived(this.contractor()!) : false,
  );
  protected sections = computed<OnyxChip<ContractorModalSection>[]>(() => {
    const contractor = this.contractor();
    return [
      {
        name: 'labels.branches',
        value: ContractorModalSection.BRANCHES,
        count: contractor ? 1 + contractor.branches.length : undefined,
      },
      ...(contractor?.pointsOfInterest.length
        ? [
            {
              name: `${this.I18N}.contractorForm.pointsOfInterest.heading`,
              value: ContractorModalSection.POINTS_OF_INTEREST,
              count: contractor?.pointsOfInterest.length,
            },
          ]
        : []),
      { name: 'labels.documents', value: ContractorModalSection.DOCUMENTS },
      {
        name: `${this.I18N}.aboutCompany`,
        value: ContractorModalSection.COMPANY,
      },
    ];
  });

  constructor() {
    this.uuid = isString(this.data) ? this.data : this.data.uuid;

    this.contractor$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.contractorsService.getContractor(this.uuid).pipe(
            catchError(() => {
              this.contractor.set(null);
              this.loading.set(false);
              this.error.set(true);

              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((contractor) => {
        this.contractor.set(contractor);
        this.loading.set(false);
        this.error.set(false);
      });

    if (isString(this.data) || !ContractorHelper.isContractorType(this.data)) {
      this.contractor$.next();
    } else {
      this.contractor.set(this.data);
    }

    this.contractorsService.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.contractor$.next());
  }

  protected block(): void {
    const contractor = this.contractor();
    if (contractor) this.contractorHelper.block(contractor);
  }

  protected unblock(): void {
    const contractor = this.contractor();
    if (contractor) this.contractorHelper.unblock(contractor.uuid);
  }

  protected edit(): void {
    const contractor = this.contractor();
    if (contractor) {
      this.contractorHelper.edit(contractor.uuid, { close$: this.close$ });
    }
  }

  protected share(): void {
    const contractor = this.contractor();
    if (contractor) this.contractorHelper.share(contractor);
  }

  protected unarchiveContractor(uuid: string): void {
    this.contractorsService.unarchiveContractor(uuid).subscribe(() => {
      this.toastService.showSuccess({
        message: `${this.I18N}.toasts.contractorUnarchived`,
      });
      this.close$.next();
    });
  }
}
